/**
 * Deterministic metric helpers for the Compare page.
 *
 * The headline chips + detailed-table best/worst dots are all driven
 * by these pure functions. No LLM input here - a green "best" dot
 * should be derivable from the JSON alone and reproducible across
 * page renders. The AI summary's verdict is rendered separately,
 * streamed, and clearly attributed to the LLM.
 */
import type { AnalysisResult, FactCheck } from "@/types";

/** Severities that count toward the "red flag" total shown on the
 *  card + headline chip. Warning / info / match are neutral or
 *  positive and would bloat the count unfairly. */
const RED_FLAG_SEVERITIES = new Set(["critical", "mismatch"]);

export function redFlagCount(result: AnalysisResult): number {
  const list: FactCheck[] = result.fact_checks || [];
  return list.reduce((acc, fc) => acc + (RED_FLAG_SEVERITIES.has(fc.status) ? 1 : 0), 0);
}

export function pricePerM2(result: AnalysisResult): number | null {
  const area = result.area_m2;
  const price = result.price;
  if (!area || !price || area <= 0) return null;
  return price / area;
}

/** Approximate "metro distance" for the headline chip. Computed
 *  features live inside the ML result but aren't typed on
 *  AnalysisResult - back off to undefined when missing. */
function metroKm(result: AnalysisResult): number | null {
  const v = (result as unknown as { computed_features?: Record<string, number> })
    .computed_features?.distance_to_metro_km;
  return typeof v === "number" ? v : null;
}

interface HeadlineWinners {
  bestOverall: number | null;
  cheapest: number | null;
  bestPpm: number | null;
  closestMetro: number | null;
  fewestFlags: number | null;
}

/** Compute all headline winners in one pass so the chip row is
 *  consistent - no risk of two metrics disagreeing about "best
 *  overall" because they were computed at different times.
 *
 *  Ties: when two or more listings tie on a metric (e.g. all three
 *  have the same `fewest_flags` count), that metric returns `null`
 *  so the chip is suppressed entirely. Picking an arbitrary index
 *  on a tie would read as "Listing 1 has the lowest risk rating"
 *  when in fact every listing matches - a lie the user would spot
 *  immediately. Delegates to `bestAndWorstIndex`, which already
 *  does the tie-aware bookkeeping for the detailed-table row dots,
 *  so both surfaces stay consistent. */
export function computeHeadlineWinners(results: AnalysisResult[]): HeadlineWinners {
  if (results.length === 0) {
    return {
      bestOverall: null, cheapest: null, bestPpm: null,
      closestMetro: null, fewestFlags: null,
    };
  }

  const opValues    = results.map((r) => (typeof r.overpriced_pct === "number" ? r.overpriced_pct : null));
  const priceValues = results.map((r) => (typeof r.price === "number" ? r.price : null));
  const ppmValues   = results.map((r) => pricePerM2(r));
  const metroValues = results.map((r) => metroKm(r));
  const flagValues  = results.map((r) => redFlagCount(r));

  return {
    bestOverall:  bestAndWorstIndex(opValues,    /* lowerIsBetter */ true).best,
    cheapest:     bestAndWorstIndex(priceValues, true).best,
    bestPpm:      bestAndWorstIndex(ppmValues,   true).best,
    closestMetro: bestAndWorstIndex(metroValues, true).best,
    fewestFlags:  bestAndWorstIndex(flagValues,  true).best,
  };
}

/** Pick the row-level "best / worst" indices for a numeric column.
 *
 * Ties: if two or more listings share the best (or worst) value, the
 * row gets no "best" (or no "worst") highlight - marking just the
 * first one would falsely imply it won. Single winner + losers is the
 * only case where both highlights fire.
 *
 * @param values - per-listing number or null. null entries never win.
 * @param lowerIsBetter - true for "overpriced_pct" (lower = better),
 *                       false for "fair value savings" (higher = better).
 */
export function bestAndWorstIndex(
  values: Array<number | null | undefined>,
  lowerIsBetter: boolean,
): { best: number | null; worst: number | null } {
  const compPairs: Array<{ idx: number; v: number }> = [];
  values.forEach((v, i) => {
    if (typeof v === "number" && Number.isFinite(v)) compPairs.push({ idx: i, v });
  });
  if (compPairs.length === 0) return { best: null, worst: null };

  // Sort ascending. `best` is first (or last) depending on
  // lowerIsBetter; `worst` is the other end.
  compPairs.sort((a, b) => a.v - b.v);
  const minVal = compPairs[0].v;
  const maxVal = compPairs[compPairs.length - 1].v;

  // If min === max every comparable value is equal - no winner and
  // no loser, not even a stable "first" one.
  if (minVal === maxVal) return { best: null, worst: null };

  const bestVal = lowerIsBetter ? minVal : maxVal;
  const worstVal = lowerIsBetter ? maxVal : minVal;
  const bestIds = compPairs.filter((p) => p.v === bestVal).map((p) => p.idx);
  const worstIds = compPairs.filter((p) => p.v === worstVal).map((p) => p.idx);

  // Ties at the top (or bottom) = no single winner/loser. Marking
  // one of several tied listings would be a lie.
  const best = bestIds.length === 1 ? bestIds[0] : null;
  const worst = worstIds.length === 1 ? worstIds[0] : null;
  return { best, worst };
}
